import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  X, 
  Download, 
  ExternalLink, 
  FileText, 
  Image as ImageIcon,
  Maximize2,
  Minimize2
} from "lucide-react";
import { useState } from "react";

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileType?: string;
}

export function DocumentPreviewModal({
  isOpen,
  onClose,
  fileUrl,
  fileName,
  fileType
}: DocumentPreviewModalProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Improved detection to handle URLs with query parameters (S3 signed URLs)
  const isImage = fileType?.startsWith("image/") || /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(fileUrl);
  const isPDF = fileType === "application/pdf" || /\.pdf(\?.*)?$/i.test(fileUrl);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] p-0 overflow-hidden flex flex-col border-none bg-white shadow-2xl">
        <DialogHeader className="p-3 bg-slate-50 border-b flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-indigo-50 rounded-lg">
              {isImage ? (
                <ImageIcon className="h-4 w-4 text-indigo-600" />
              ) : (
                <FileText className="h-4 w-4 text-indigo-600" />
              )}
            </div>
            <div>
              <DialogTitle className="text-slate-900 text-sm font-bold truncate max-w-[400px]">
                {fileName}
              </DialogTitle>
            </div>
          </div>

        </DialogHeader>

        <div className="flex-1 overflow-hidden bg-slate-200">
          {isImage ? (
            <div className="w-full h-full flex items-center justify-center p-4">
              <img
                src={fileUrl}
                alt={fileName}
                className="max-w-full max-h-full object-contain shadow-lg bg-white"
              />
            </div>
          ) : isPDF ? (
            <iframe
              src={fileUrl}
              className="w-full h-full border-none"
              title={fileName}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center p-4 bg-white">
              <div className="text-center space-y-4 max-w-sm">
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto">
                  <FileText className="h-8 w-8 text-slate-400" />
                </div>
                <div>
                  <h3 className="text-slate-900 font-bold text-base">Vista previa no disponible</h3>
                  <p className="text-slate-500 text-xs mt-1">
                    Este tipo de archivo no admite vista previa directa. Por favor, descárgalo.
                  </p>
                </div>
                <Button 
                  onClick={handleDownload}
                  className="bg-slate-900 hover:bg-black text-white w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar Archivo
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
